import { useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { useNavigate } from '@/hooks/use-navigate';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import {
    buildRefinedSearch,
    buildSortedSearch,
    clearRefinements,
    getAppliedFilters,
    getFilterGroups,
    isRefinementSelected,
    removeRefinement,
    toggleRefinement,
} from '../lib/refinements';

type ProductSearchResult = ShopperSearch.schemas['ProductSearchResult'];

/**
 * URL-driven filter + sort state for a PLP. Every change navigates immediately (the loader re-runs), and reads
 * come from the in-flight navigation target so checkboxes/pills react before the new results land.
 */
export function usePlpFilters(result: ProductSearchResult) {
    const { t } = useTranslation('plpFilters');
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();

    const pendingSearch = navigation.location?.pathname === location.pathname ? navigation.location.search : undefined;
    const effectiveSearch = pendingSearch ?? location.search;
    const isPending = pendingSearch !== undefined;

    const refines = useMemo(
        () => new URLSearchParams(effectiveSearch).getAll(PRODUCT_SEARCH_QUERY_PARAMS.REFINE),
        [effectiveSearch]
    );

    const selectedSort =
        new URLSearchParams(effectiveSearch).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT) || result.selectedSortingOption;

    const groups = useMemo(() => getFilterGroups(result.refinements), [result.refinements]);
    const appliedFilters = useMemo(
        () => getAppliedFilters(refines, result.refinements, t('inStockOnly')),
        [refines, result.refinements, t]
    );

    const go = useCallback(
        (search: string): void => {
            void navigate({ pathname: location.pathname, search }, { preventScrollReset: true });
        },
        [location.pathname, navigate]
    );

    const applyRefines = useCallback(
        (next: string[]): void => go(buildRefinedSearch(effectiveSearch, next)),
        [effectiveSearch, go]
    );

    return {
        refines,
        groups,
        appliedFilters,
        total: result.total,
        isPending,
        sortingOptions: result.sortingOptions ?? [],
        selectedSort,
        isSelected: (attributeId: string, value: string) => isRefinementSelected(refines, attributeId, value),
        toggle: (attributeId: string, value: string) => applyRefines(toggleRefinement(refines, attributeId, value)),
        remove: (attributeId: string, value: string) => applyRefines(removeRefinement(refines, attributeId, value)),
        clearAll: () => applyRefines(clearRefinements(refines)),
        applyRefines,
        setSort: (sort: string) => go(buildSortedSearch(effectiveSearch, sort)),
    };
}

export type PlpFiltersState = ReturnType<typeof usePlpFilters>;
